import type { Filters } from '../home.ts'
import type { AppContext } from 'types/common.js'

import { graphql } from '$houdini'
import { getSession } from 'stores/session'
import component from './TransferOrder.svelte'
import { stringToBoolean as toBool } from '@isoftdata/utility-string'
import { datesFromRange, type NamedDateRange } from '@isoftdata/utility-date-time'

export interface TransferOrderFilters extends Filters {
	showDoneReceiving: boolean
}

const transferOrderQuery = graphql(`
	query HomeTransferOrders($filter: TransferOrderFilter) {
		transferOrders(filter: $filter) {
			items {
				id
				date
				destinationComment
				destinationStore {
					code
					name
				}
				doneReceiving
				ordered
				sourceComment
				sourceStore {
					code
					name
				}
				timeEntered
				user {
					name
				}
			}
		}
	}
`)

const defaults = graphql(`
	query HomeTransferOrderDefaults {
		settingValues {
			home {
				defaultTransferOrderHistoryDateRange
			}
		}
	}
`)

export default function ({ stateRouter }: AppContext) {
	stateRouter.addState({
		name: 'app.home.transfer-order',
		route: 'transfer-order',
		querystringParameters: ['fromDate', 'toDate', 'userId', 'storeId', 'showDoneReceiving'],
		defaultParameters: {
			userId: () => getSession()?.userAccountId?.toString() ?? null,
			storeId: () => getSession()?.currentStore?.toString() ?? null,
			showDoneReceiving: 'false',
			showPricesFinalized: 'false',
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(data, parameters) {
			if (parameters.fromDate === undefined && parameters.toDate === undefined) {
				const res = await defaults.fetch()
				const defaultDateRange = datesFromRange(
					(res?.data?.settingValues?.home?.defaultTransferOrderHistoryDateRange as NamedDateRange) || 'Last 30 Days',
				)

				throw {
					redirectTo: {
						name: null,
						params: {
							fromDate: defaultDateRange.from,
							toDate: defaultDateRange.to,
						},
					},
				}
			}

			const filters: TransferOrderFilters = {
				userId: parameters.userId ? parseInt(parameters.userId, 10) || undefined : undefined,
				storeId: parameters.storeId ? parseInt(parameters.storeId, 10) || undefined : undefined,
				showDoneReceiving: toBool(parameters.showDoneReceiving),
				dates: {
					from: parameters.fromDate,
					to: parameters.toDate,
				},
			}

			const res = await transferOrderQuery.fetch({
				variables: {
					filter: {
						date: {
							gte: filters.dates.from ? new Date(filters.dates.from) : undefined,
							lte: filters.dates.to ? new Date(filters.dates.to) : undefined,
						},
						doneReceiving: filters.showDoneReceiving ? null : false,
						createdByUser: { id: filters.userId ? [filters.userId] : undefined },
						ownerStore: {
							id: filters.storeId ? [filters.storeId] : undefined,
						},
					},
				},
			})

			return {
				transferOrders: res?.data?.transferOrders?.items ?? [],
				...filters,
			}
		},
	})
}
